import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AirIcon from '@mui/icons-material/Air';

/* Contenedor centrado y responsive */
const containerSx = (hideBelowWidth = 0) => ({
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
  mt: -1,
  opacity: 1,
  transition: 'opacity .25s ease',
  [`@media (max-width:${hideBelowWidth}px)`]: { opacity: 0 },
});

/* Niveles Turbulence (colores sólidos) */
const LEVELS = [
  { label: 'Light',          code: 'LGT',     color: '#fdde3d', text: '#0B1220' },
  { label: 'Moderate-Severe',code: 'MOD-SEV', color: '#D32F2F', text: '#FFFFFF' },
];

/* Texto blanco/negro automático por contraste (YIQ) */
function textColorFor(hex) {
  if (!hex || hex.length < 6) return "#0B1220";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#0B1220" : "#FFFFFF";
}

export default function RiskSelectorTurbulence({
  levels = LEVELS,
  hideBelowWidth = 0,
  interactive = false,
  onSelect,
  size = 'mini',        // 'mini' | 'md'
  equalWidth = true,
  showTitle = true,
  titleText = 'Turbulence level',
}) {
  const isMini = size === 'mini';
  const pillMin = equalWidth ? (isMini ? 108 : 124) : 0;

  return (
    <Box>
      {/* Título sutil centrado con icono a la izquierda */}
      {showTitle && (
        <Box sx={{ display: 'grid', justifyItems: 'center', pointerEvents: 'none', mb: 0.9 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              typography: 'caption',
              fontWeight: 800,
              letterSpacing: '.025em',
              textTransform: 'uppercase',
              color: '#23339b',
              lineHeight: 1.05,
            }}
          >
            <AirIcon sx={{ fontSize: { xs: 14, sm: 14, md: 15 }, color: 'inherit' }} aria-hidden />
            {titleText}
          </Box>
        </Box>
      )}

      {/* Dos casillas simétricas tipo píldora */}
      <Box sx={containerSx(hideBelowWidth)}>
        <Box
          sx={{
            display: 'inline-flex',
            borderRadius: 999,
            overflow: 'hidden',
            pointerEvents: interactive ? 'auto' : 'none',
          }}
        >
          {levels.map((lvl, i) => {
            const isLast = i === levels.length - 1; // evitar “borroso” en la última
            return (
              <Button
                key={lvl.code}
                disableElevation
                disableRipple
                aria-label={lvl.label}
                onClick={interactive && onSelect ? () => onSelect(lvl.code) : undefined}
                sx={{
                  position: 'relative',
                  minWidth: equalWidth ? (isMini ? 74 : 88) : 0,
                  px: isMini ? 1.0 : 1.2,
                  py: isMini ? 0.44 : 0.58,
                  fontSize: isMini ? '.62rem' : '.7rem',
                  fontWeight: 600,             
                  letterSpacing: '.02em',
                  lineHeight: 1,
                  textTransform: 'none',
                  color: textColorFor(lvl.color),
                  backgroundColor: `rgba(${parseInt(lvl.color.slice(1, 3), 16)}, ${parseInt(lvl.color.slice(3, 5), 16)}, ${parseInt(lvl.color.slice(5, 7), 16)}, 0.8)`,
                  backdropFilter: "blur(20px) saturate(190%) contrast(1.1)",
                  borderRadius: 0,
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  borderTop: "2.5px solid rgba(255, 255, 255, 0.5)",
                  borderRight: i < levels.length - 1 ? 0 : "1px solid rgba(255, 255, 255, 0.25)",
                  boxShadow: `
                    0 12px 40px 0 rgba(0, 0, 0, 0.25),
                    inset 0 1px 1px 0 rgba(255, 255, 255, 0.3),
                    inset 0 -1px 0px 0 rgba(0, 0, 0, 0.1)
                  `,
                  transition: 'transform .2s cubic-bezier(0.4, 0, 0.2, 1), background-color .25s ease, box-shadow .2s ease',
                  cursor: interactive ? 'pointer' : 'default',
                  '&:hover': {
                    transform: interactive ? 'scale(1.02) translateY(-2px)' : 'none',
                    backgroundColor: `rgba(${parseInt(lvl.color.slice(1, 3), 16)}, ${parseInt(lvl.color.slice(3, 5), 16)}, ${parseInt(lvl.color.slice(5, 7), 16)}, 0.9)`,
                    boxShadow: "0 15px 45px 0 rgba(0, 0, 0, 0.3)",
                    zIndex: 2,
                  },
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                  textRendering: 'optimizeLegibility',
                }}
              >
                {lvl.label}
              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
